import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { TrashEntry } from '../models/trash-entry.model';

const STORAGE_KEY = 'forms-builder-trash';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class FormsBuilderTrashService {
  private trashSubject = new BehaviorSubject<TrashEntry[]>([]);
  trash$: Observable<TrashEntry[]> = this.trashSubject.asObservable();

  constructor() {
    this.loadFromLocalStorage();
  }

  get trashEntries(): TrashEntry[] {
    return this.trashSubject.getValue();
  }

  addDeletedForm(formId: string, formName: string): void {
    const entries = this.trashSubject.getValue().filter(e => e.id !== formId);
    entries.push({
      type: 'form',
      id: formId,
      name: formName,
      deletedOn: new Date().toISOString()
    });
    this.trashSubject.next(entries);
    this.saveToLocalStorage();
  }

  addDeletedVersion(
    versionId: string,
    versionLabel: string,
    parentFormId: string,
    parentFormName: string
  ): void {
    const entries = this.trashSubject.getValue().filter(e => e.id !== versionId);
    entries.push({
      type: 'version',
      id: versionId,
      name: versionLabel,
      parentFormId,
      parentFormName,
      deletedOn: new Date().toISOString()
    });
    this.trashSubject.next(entries);
    this.saveToLocalStorage();
  }

  removeEntry(id: string): void {
    const entries = this.trashSubject.getValue().filter(e => e.id !== id);
    this.trashSubject.next(entries);
    this.saveToLocalStorage();
  }

  clearAllTrash(): void {
    this.trashSubject.next([]);
    this.saveToLocalStorage();
  }

  removeEntriesForForm(formId: string): void {
    const entries = this.trashSubject.getValue().filter(
      e => !(e.id === formId || (e.type === 'version' && e.parentFormId === formId))
    );
    this.trashSubject.next(entries);
    this.saveToLocalStorage();
  }

  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.trashSubject.getValue()));
    } catch (e) {
      console.error('Error saving trash to localStorage:', e);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const entries: TrashEntry[] = JSON.parse(raw);
        const validEntries = entries.filter(e => this.isWithinRetentionPeriod(e));
        this.trashSubject.next(validEntries);
        if (validEntries.length !== entries.length) {
          this.saveToLocalStorage();
        }
      }
    } catch (e) {
      console.error('Error loading trash from localStorage:', e);
      this.trashSubject.next([]);
    }
  }

  private isWithinRetentionPeriod(entry: TrashEntry): boolean {
    const deletedDate = new Date(entry.deletedOn).getTime();
    return (Date.now() - deletedDate) < THIRTY_DAYS_MS;
  }
}
